import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import "../../Landing.css";
import "./SignUpForm.css";
import { useShoppingContext } from "../../../context/ShoppingContext";

const SignUpForm = () => {
  const { handleLogin } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    nombre: "",
    ubicacion: "",
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [step, setStep] = useState(1); // Paso actual del formulario
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false); // Estado de carga
  const { usuarios, fetchUsuarios, fetchMisGranjas, isDarkMode } =
    useShoppingContext();

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Validaciones para los datos de la granja
  const validateStepOne = () => {
    let formErrors = {};

    if (!formData.nombre.trim()) {
      formErrors.nombre = "El nombre de la granja es obligatorio.";
    }

    if (!formData.ubicacion.trim()) {
      formErrors.ubicacion = "La ubicación de la granja es obligatoria.";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // Validaciones para los datos del usuario
  const validateStepTwo = () => {
    let formErrors = {};

    if (!formData.username.trim()) {
      formErrors.username = "El nombre de usuario es obligatorio.";
    } else if (usuarios.some((user) => user.username === formData.username)) {
      formErrors.username = "Este nombre de usuario ya está en uso.";
    }

    if (!formData.email.trim()) {
      formErrors.email = "El correo electrónico es obligatorio.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      formErrors.email = "El correo electrónico no es válido.";
    } else if (usuarios.some((user) => user.email === formData.email)) {
      formErrors.email = "Este correo electrónico ya está registrado.";
    }

    if (!formData.password.trim()) {
      formErrors.password = "La contraseña es obligatoria.";
    } else if (formData.password.length < 8) {
      formErrors.password = "La contraseña debe tener al menos 8 caracteres.";
    }

    if (!formData.confirmPassword.trim()) {
      formErrors.confirmPassword =
        "La confirmación de contraseña es obligatoria.";
    } else if (formData.password !== formData.confirmPassword) {
      formErrors.confirmPassword = "Las contraseñas no coinciden.";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStepTwo()) {
      setLoading(true); // Activar estado de carga
      try {
        // Registro del usuario
        const userResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/users`,
          formData
        );

        // Auto-login y obtener el usuario autenticado
        const loginResponse = await handleLogin({
          email: formData.email,
          password: formData.password,
        });

        // Creación de la granja asociada al usuario
        await axios.post(`${process.env.REACT_APP_API_URL}/granjas`, {
          nombre: formData.nombre,
          ubicacion: formData.ubicacion,
          userId: userResponse.data.user.id,
        });

        // Navegar a la página de las granjas
        fetchMisGranjas();
        navigate(`/mis-granjas`);
        setMessage("¡Éxito! Tu cuenta y granja han sido creadas.");
      } catch (error) {
        setMessage(
          "Hubo un problema al registrarse. Por favor, inténtalo de nuevo."
        );
        console.error(
          "Error durante el registro:",
          error.response?.data || error.message
        );
      } finally {
        setLoading(false); // Desactivar estado de carga
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  const nextStep = () => {
    if (validateStepOne()) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  return (
    <form
      className={isDarkMode ? "signUp-form" : "signUp-form light"}
      onSubmit={handleSubmit}
    >
      {step === 1 && (
        <>
          <h1 className={isDarkMode ? "signUp-title" : "signUp-title light"}>
            CREAR GRANJA
          </h1>
          <div className="signUp-row">
            <div className="signUp-group">
              <label
                htmlFor="nombre"
                className={isDarkMode ? "signUp-label" : "signUp-label light"}
              >
                Nombre de la Granja
              </label>
              <input
                type="text"
                id="nombre"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                required
                className={isDarkMode ? "signUp-input" : "signUp-input light"}
                placeholder=""
              />
              {errors.nombre && <p className="error-text">{errors.nombre}</p>}
            </div>
            <div className="signUp-group">
              <label
                htmlFor="ubicacion"
                className={isDarkMode ? "signUp-label" : "signUp-label light"}
              >
                Ubicación de la Granja
              </label>
              <input
                type="text"
                id="ubicacion"
                name="ubicacion"
                value={formData.ubicacion}
                onChange={handleChange}
                required
                className={isDarkMode ? "signUp-input" : "signUp-input light"}
                placeholder="Ciudad o región"
              />
              {errors.ubicacion && (
                <p className="error-text">{errors.ubicacion}</p>
              )}
            </div>
          </div>
          <div className="btns-right">
            <button
              type="button"
              onClick={nextStep}
              className="signUp-btn-green btn-now"
            >
              Siguiente: Crear Cuenta
            </button>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <h1 className={isDarkMode ? "signUp-title" : "signUp-title light"}>
            GUARDAR PROGRESO
          </h1>
          <div className="signUp-row">
            <div className="signUp-group">
              <label
                htmlFor="username"
                className={isDarkMode ? "signUp-label" : "signUp-label light"}
              >
                Nombre de Usuario
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                required
                className={isDarkMode ? "signUp-input" : "signUp-input light"}
                placeholder="Elige un nombre único"
              />
              {errors.username && (
                <p className="error-text">{errors.username}</p>
              )}
            </div>
            <div className="signUp-group">
              <label
                htmlFor="email"
                className={isDarkMode ? "signUp-label" : "signUp-label light"}
              >
                Correo Electrónico
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={isDarkMode ? "signUp-input" : "signUp-input light"}
                placeholder="Tu email de contacto"
              />
              {errors.email && <p className="error-text">{errors.email}</p>}
            </div>
          </div>

          <div className="signUp-row">
            <div className="signUp-group">
              <label
                htmlFor="password"
                className={isDarkMode ? "signUp-label" : "signUp-label light"}
              >
                Contraseña
              </label>
              <div className="password-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className={isDarkMode ? "signUp-input" : "signUp-input light"}
                  placeholder="Mínimo 8 caracteres"
                />
                <FontAwesomeIcon
                  icon={showPassword ? faEyeSlash : faEye}
                  className="password-icon"
                  onClick={togglePasswordVisibility}
                />
              </div>
              {errors.password && (
                <p className="error-text">{errors.password}</p>
              )}
            </div>

            <div className="signUp-group">
              <label
                htmlFor="confirmPassword"
                className={isDarkMode ? "signUp-label" : "signUp-label light"}
              >
                Repite tu Contraseña
              </label>
              <div className="password-container">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className={isDarkMode ? "signUp-input" : "signUp-input light"}
                  placeholder="Repite tu contraseña"
                />
                <FontAwesomeIcon
                  icon={showConfirmPassword ? faEyeSlash : faEye}
                  className="password-icon"
                  onClick={toggleConfirmPasswordVisibility}
                />
              </div>
              {errors.confirmPassword && (
                <p className="error-text">{errors.confirmPassword}</p>
              )}
            </div>
          </div>

          <div className="flex-column">
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
                Cargando...
              </div>
            ) : (
              <button type="submit" className="signUp-btn-green btn-now">
                Finalizar Registro
              </button>
            )}

            <button
              type="button"
              onClick={prevStep}
              className="btn btn-back"
              style={{ marginBottom: "5px", marginTop: "15px" }}
            >
              Paso anterior: Crear Granja
            </button>
          </div>
        </>
      )}

      {message && <p className="form-message">{message}</p>}
    </form>
  );
};

export default SignUpForm;
